/*
 * KANBAN SERVICE
 * 
 * Service layer for Kanban board API operations:
 * - CRUD operations for persisted Kanban boards
 * - Schema-based access control enforced server-side
 * - Board configuration stored as JSON in database
 * 
 * Used by:
 * - Kanban.tsx: Main page for loading/saving boards
 * - KanbanEditor.tsx: Board creation and updates
 */

import { request } from './dataService';
import { API_ENDPOINTS } from '../constants/apiEndpoints';
import { KanbanBoard, KanbanConfig } from '../models/kanban';

export interface CreateKanbanBoardInput {
  schemaId: number;
  name: string;
  config: KanbanConfig;
}

export interface UpdateKanbanBoardInput {
  name?: string;
  config?: KanbanConfig;
}

/**
 * Get all Kanban boards accessible to current user
 * (filtered by schema access server-side)
 */
export async function getKanbanBoards(): Promise<KanbanBoard[]> {
  return request(API_ENDPOINTS.GET_KANBAN_BOARDS) as Promise<KanbanBoard[]>;
}

/**
 * Get a single Kanban board by ID
 */
export async function getKanbanBoard(id: number): Promise<KanbanBoard> {
  const endpoint = API_ENDPOINTS.GET_KANBAN_BOARD.replace(':id', id.toString());
  return request(endpoint, { id }) as Promise<KanbanBoard>;
}

/**
 * Get all Kanban boards for a specific schema
 */
export async function getKanbanBoardsBySchema(schemaId: number): Promise<KanbanBoard[]> {
  const endpoint = API_ENDPOINTS.GET_KANBAN_BOARDS_BY_SCHEMA.replace(':schemaId', schemaId.toString());
  return request(endpoint, { schemaId }) as Promise<KanbanBoard[]>;
}

/**
 * Create a new Kanban board
 */
export async function createKanbanBoard(input: CreateKanbanBoardInput): Promise<KanbanBoard> {
  return request(API_ENDPOINTS.POST_KANBAN_BOARD, {
    schemaId: input.schemaId,
    name: input.name,
    config: JSON.stringify(input.config)
  }) as Promise<KanbanBoard>;
}

/**
 * Update an existing Kanban board
 */
export async function updateKanbanBoard(id: number, input: UpdateKanbanBoardInput): Promise<KanbanBoard> {
  const endpoint = API_ENDPOINTS.PATCH_KANBAN_BOARD.replace(':id', id.toString());

  const body: Record<string, any> = { id };
  if (input.name !== undefined) {
    body.name = input.name;
  }
  if (input.config !== undefined) {
    body.config = JSON.stringify(input.config);
  }

  return request(endpoint, body) as Promise<KanbanBoard>;
}

/**
 * Delete a Kanban board
 */
export async function deleteKanbanBoard(id: number): Promise<void> {
  const endpoint = API_ENDPOINTS.DELETE_KANBAN_BOARD.replace(':id', id.toString());
  return request(endpoint, { id }) as Promise<void>;
}

/**
 * Parse the config JSON from a KanbanBoard entity
 */
export function parseKanbanConfig(board: KanbanBoard): KanbanConfig {
  return JSON.parse(board.config);
}

/**
 * Helper to get board with parsed config
 */
export interface KanbanBoardWithConfig extends Omit<KanbanBoard, 'config'> {
  config: KanbanConfig;
}

export async function getKanbanBoardWithConfig(id: number): Promise<KanbanBoardWithConfig> {
  const board = await getKanbanBoard(id);
  return {
    ...board,
    config: parseKanbanConfig(board)
  };
}
